/**
 * The /overview facts read (spec §2.1): get_overview_facts for a scope and a
 * range, paged — PostgREST answers an unpaged read with at most 1000 rows,
 * HTTP 200 and no error. Ordered by the RPC's whole GROUP BY key, so pages
 * never overlap or skip. THROWS on any failed page: a partial read would show
 * as smaller totals nobody measured. Runs on the browser client (the caller's RLS).
 *
 * @module services/overview/overviewFacts
 */
import { getSupabaseClient } from '@/config';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { nullableNumber } from './overviewMath';
import type { FactRow } from './overviewTypes';

/** A get_overview_facts row as PostgREST returns it: numeric may arrive as a string. */
export interface FactDb {
  date: string;
  workspace_id: string;
  platform: string;
  objective: string;
  account_id: string | null;
  account_name: string | null;
  currency: string | null;
  spend: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  conversions: number | string | null;
  conversion_value: number | string | null;
  reach: number | string | null;
}

export const factOf = (r: FactDb): FactRow => ({
  date: r.date,
  workspaceId: r.workspace_id,
  platform: r.platform,
  objective: r.objective,
  accountId: r.account_id,
  accountName: r.account_name,
  // char(3)-style padding would make 'THB ' a second currency.
  currency: r.currency ? r.currency.trim() || null : null,
  spend: nullableNumber(r.spend),
  impressions: nullableNumber(r.impressions),
  clicks: nullableNumber(r.clicks),
  conversions: nullableNumber(r.conversions),
  conversionValue: nullableNumber(r.conversion_value),
  reach: nullableNumber(r.reach),
});

export const readOverviewFacts = async (workspaceIds: readonly string[], range: DateRange): Promise<FactRow[]> => {
  if (workspaceIds.length === 0) return [];
  const { rows, error } = await readAllRows<FactDb>(
    (from, to) =>
      getSupabaseClient()
        .rpc('get_overview_facts', { p_workspace_ids: [...workspaceIds], p_start: range.start, p_end: range.end })
        .order('date', { ascending: true })
        .order('platform', { ascending: true })
        .order('objective', { ascending: true })
        .order('account_id', { ascending: true })
        .order('workspace_id', { ascending: true })
        .range(from, to),
    'readOverviewFacts',
  );
  if (error) throw new Error(`overview facts read failed: ${errorMessageOf(error)}`);
  return rows.map(factOf);
};
